import RiArrowLeftLine from '@ben/icons/arrow-left-s-line';
import RiArrowRightLine from '@ben/icons/arrow-right-s-line';
import { Skeleton } from '@mantine/core';
import type { ReactNode } from 'react';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import Button, { ButtonVariant } from '../deprecated-button/button';
import Drawer from '../drawer';
import IconButton from '../icon-button';
import Typography from '../typography/typography';

interface Labels {
  leftArrowLabel: string;
  rightArrowLabel: string;
}

export interface GalleryDrawerProps {
  title: string;
  isOpen?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  onClose?: () => void;
  isLoading?: boolean;
  labels: Labels;
  children: ReactNode;
}

const GalleryDrawer = ({
  title,
  onNext,
  onPrevious,
  onClose,
  isLoading = false,
  labels,
  children,
  isOpen = true,
}: GalleryDrawerProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(isOpen);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const { leftArrowLabel, rightArrowLabel } = labels;

  const handleOnClose = () => {
    setOpen(false);
    onClose?.();
  };
  return (
    <Drawer opened={open} onClose={handleOnClose} data-testid="gallery-drawer">
      <StyledDrawerHeader>
        {isLoading ? (
          <Skeleton width={200} height={theme.typography.h2.fontSize} />
        ) : (
          <Typography variant={TypographyVariant.h2}>{title}</Typography>
        )}
        <NavButtons>
          <IconButton
            icon={RiArrowLeftLine}
            onClick={onPrevious}
            disabled={!onPrevious}
            aria-label={leftArrowLabel}
            data-testid="ArrowsLeft-icon-button"
          />
          <IconButton
            icon={RiArrowRightLine}
            onClick={onNext}
            disabled={!onNext}
            aria-label={rightArrowLabel}
            data-testid="ArrowsRight-icon-button"
          />
        </NavButtons>
      </StyledDrawerHeader>
      <StyledDrawerBody>{children}</StyledDrawerBody>
      <StyledDrawerFooter>
        <Button
          label={t('close', { defaultValue: 'Close' })}
          onClick={handleOnClose}
          variant={ButtonVariant.secondary}
        />
      </StyledDrawerFooter>
    </Drawer>
  );
};

export default memo(GalleryDrawer);

const StyledDrawerHeader = styled(Drawer.Header)`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
`;

const NavButtons = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(2)};
  `}
`;

const StyledDrawerFooter = styled(Drawer.Footer)`
  display: flex;
  justify-content: center;
`;

const StyledDrawerBody = styled(Drawer.Body)`
  ${({ theme }) => css`
    padding: ${theme.spacing(2, 0, 0, 0)};
  `}
`;
